import React, { useState, useEffect } from "react";
import { FiUsers, FiClock, FiPlus, FiSave, FiEdit2, FiTrash2, FiX, FiCheckCircle } from "react-icons/fi";
import moment from "moment";
import Select from "react-select";
import { commonSelectProps } from "../../../components/shared/SelectStyles";
import { userRequest, publicRequest } from "../../../requestMethods";
import { useSelector } from "react-redux";
import StepCodeTag from "../../../components/shared/StepCodeTag";
import PosFieldLabel from "../../../components/shared/PosFieldLabel";
import { JOURNAL_STEP_CODES } from "../../../constants/workflowStepCodes";

const JournalTab = ({
    orderData,
    installerManager,
    productionManager,
    manager,
    currentUser,
    outright,
    setOrderData,
}) => {
    // Filter to only show active journals
    const journals = (orderData?.journals || []).filter(journal => journal.status !== "inactive");
    const [showForm, setShowForm] = useState(false);
    const [installersOptions, setInstallersOptions] = useState([]);
    const [selectedInstallers, setSelectedInstallers] = useState([]);
    const [installationDate, setInstallationDate] = useState("");
    const [installationTime, setInstallationTime] = useState("");
    const [journalType, setJournalType] = useState("installation");
    const [isLoading, setIsLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedJournal, setSelectedJournal] = useState(null);
    const [editFormData, setEditFormData] = useState({
        installers: [],
        createdAt: '',
        type: 'installation',
    });

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );

    const staff = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "staff"
            : null
    );

    // Check if user can create journals (installerManager OR manager OR productionManager NOT with principle 63429be7a4b0715d61b8955c, or outright)
    const canCreateJournal = superadmin || installerManager || manager || outright ||
                            (productionManager && currentUser?.principle !== "63429be7a4b0715d61b8955c");

    // Check if user can edit/delete journals
    const canEditJournal = superadmin || staff || installerManager || manager;

    // Fetch installers on component mount
    useEffect(() => {
        const fetchInstallers = async () => {
            try {
                // Determine principle parameter based on user type
                let principleParam;
                
                if (currentUser) {
                    if (currentUser.type === "outright") {
                        principleParam = currentUser._id;
                    } else if (currentUser.isSuperAdmin) {
                        principleParam = undefined; // SuperAdmin can see all installers
                    } else {
                        principleParam = currentUser.principle;
                    }
                }

                const params = {
                    type: "installer",
                    isAdmin: true,
                };
                
                // Add principle filter if not superadmin
                if (principleParam !== undefined) {
                    params.principle = principleParam;
                }

                const res = await publicRequest.get("/users/admin", {
                    params,
                });
                
                const formattedInstallers = res.data.map((installer) => ({
                    label: installer.name,
                    value: installer._id,
                }));
                
                setInstallersOptions(formattedInstallers);
            } catch (error) {
                console.error("Error fetching installers:", error);
            }
        };

        fetchInstallers();
    }, [currentUser]);

    const handleCreateJournal = async () => {
        // Validation
        if (selectedInstallers.length === 0) {
            alert("Please select at least one installer");
            return;
        }

        setIsLoading(true);
        try {
            // Combine date and time into a single Date object
            let createdAt = new Date();
            if (installationDate) {
                if (installationTime) {
                    const dateTimeString = `${installationDate}T${installationTime}`;
                    createdAt = new Date(dateTimeString);
                } else {
                    const dateOnly = new Date(installationDate);
                    const now = new Date();
                    dateOnly.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
                    createdAt = dateOnly;
                }
            }

            const journalData = {
                order: orderData._id,
                installers: selectedInstallers.map((inst) => inst.value),
                createdAt: createdAt.toISOString(),
                type: journalType,
            };

            await userRequest.post("/journal", journalData);

            alert("Journal created successfully!");
            // Reset form
            setSelectedInstallers([]);
            setInstallationDate("");
            setInstallationTime("");
            setJournalType("installation");
            setShowForm(false);
            // Reload the page to show the new journal
            window.location.reload();
        } catch (error) {
            console.error("Error creating journal:", error);
            alert(error.response?.data || "Failed to create journal. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditJournal = (journal) => {
        setSelectedJournal(journal);
        setEditFormData({
            installers: journal.installers?.map(inst => inst._id || inst) || [],
            createdAt: moment(journal.createdAt).format("YYYY-MM-DDTHH:mm"),
            type: journal.type || "installation",
        });
        setEditModalOpen(true);
    };

    const handleUpdateJournal = async () => {
        if (!selectedJournal) return;
        
        try {
            if (editFormData.installers.length === 0) {
                alert("Please select at least one installer");
                return;
            }

            const updateData = {
                installers: editFormData.installers,
                createdAt: new Date(editFormData.createdAt),
                type: editFormData.type,
            };

            await userRequest.put(`/journal/${selectedJournal._id}`, updateData);
            
            alert("Journal updated successfully");
            setEditModalOpen(false);
            setSelectedJournal(null);
            window.location.reload();
        } catch (error) {
            console.error("Error updating journal:", error);
            alert("Failed to update journal");
        }
    };

    const handleCompleteInstallation = async () => {
        if (!orderData?._id) {
            alert("Order data not available");
            return;
        }

        if (
            !window.confirm(
                "Mark this order as installing? This will update the order status."
            )
        ) {
            return;
        }

        setIsLoading(true);
        try {
            const res = await userRequest.put(
                `/orders/updateStatus/${orderData._id}`,
                { status: "installing" }
            );

            if (res.data) {
                setOrderData?.((prev) => ({
                    ...prev,
                    status: "installing",
                    installingDate:
                        res.data.installingDate || prev?.installingDate,
                }));
                alert("Order status updated to 'installing'.");
            } else {
                alert("Failed to update order status - no response data");
            }
        } catch (error) {
            console.error("Error completing installation:", error);
            alert("Failed to update order status. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteJournal = async (journalId) => {
        if (!window.confirm("Are you sure you want to mark this journal as inactive?")) {
            return;
        }

        try {
            await userRequest.put(`/journal/delete/${journalId}`);
            alert("Journal marked as inactive");
            window.location.reload();
        } catch (error) {
            console.error("Error deleting journal:", error);
            alert("Failed to delete journal");
        }
    };

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 flex-wrap">
                            Installation Journals
                        </h1>
                        <p className="text-gray-600 text-sm">
                            View all installation journals and installer assignments for this order
                        </p>
                    </div>
                    {(canCreateJournal || installerManager) && (
                        <div className="flex flex-wrap items-center gap-3 mt-4 sm:mt-0">
                            {canCreateJournal && (
                                <button
                                    onClick={() => setShowForm(!showForm)}
                                    className="group flex items-center gap-2 justify-center w-half sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transform hover:scale-105 transition-all duration-200"
                                >
                                    <StepCodeTag code={JOURNAL_STEP_CODES.newJournal} />
                                    <FiPlus className="h-5 w-5 group-hover:animate-pulse" />
                                    <span className="font-semibold">
                                        {showForm ? "Cancel" : "New Journal"}
                                    </span>
                                </button>
                            )}
                            {installerManager &&
                                orderData?.status !== "installing" &&
                                orderData?.status !== "installApproved" &&
                                orderData?.status !== "completed" &&
                                orderData?.status !== "void" && (
                                    <button
                                        onClick={handleCompleteInstallation}
                                        disabled={isLoading}
                                        className="group flex items-center gap-2 justify-center w-half sm:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-4 focus:ring-green-300 transform hover:scale-105 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                                    >
                                        <FiCheckCircle className="h-5 w-5 group-hover:animate-pulse" />
                                        <span className="font-semibold">
                                            Complete Installation
                                        </span>
                                    </button>
                                )}
                        </div>
                    )}
                </div>
            </div>

            {/* New Journal Form */}
            {canCreateJournal && showForm && (
                <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">
                        Create New Journal
                    </h3>
                    
                    {/* Installers Selection */}
                    <div className="mb-6">
                        <PosFieldLabel code={JOURNAL_STEP_CODES.selectInstallers} className="block text-sm font-semibold text-gray-700 mb-2">
                            <FiUsers className="inline mr-2" />
                            Select Installers *
                        </PosFieldLabel>
                        <Select
                            options={installersOptions}
                            value={selectedInstallers}
                            onChange={(selected) => setSelectedInstallers(selected || [])}
                            placeholder="Select one or more installers..."
                            isMulti
                            {...commonSelectProps}
                        />
                        <span className="text-xs text-gray-500 mt-1 block">
                            Select all installers assigned to this job
                        </span>
                    </div>

                    {/* Installation Date and Time */}
                    <div className="mb-6">
                        <PosFieldLabel code={JOURNAL_STEP_CODES.installationDateTime} className="block text-sm font-semibold text-gray-700 mb-2">
                            <FiClock className="inline mr-2" />
                            Installation Date & Time
                        </PosFieldLabel>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <input
                                    type="date"
                                    value={installationDate}
                                    onChange={(e) => setInstallationDate(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                                <span className="text-xs text-gray-500 mt-1 block">
                                    Select installation date
                                </span>
                            </div>
                            <div>
                                <input
                                    type="time"
                                    value={installationTime}
                                    onChange={(e) => setInstallationTime(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                                <span className="text-xs text-gray-500 mt-1 block">
                                    Select installation time
                                </span>
                            </div>
                        </div>
                        <span className="text-xs text-gray-500 mt-1 block">
                            Leave empty to use current date and time
                        </span>
                    </div>

                    {/* Type Selection */}
                    <div className="mb-6">
                        <PosFieldLabel code={JOURNAL_STEP_CODES.journalType} className="block text-sm font-semibold text-gray-700 mb-2">
                            Type *
                        </PosFieldLabel>
                        <div className="flex gap-4">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="journalType"
                                    value="installation"
                                    checked={journalType === "installation"}
                                    onChange={(e) => setJournalType(e.target.value)}
                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-gray-700">Installation</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="journalType"
                                    value="service"
                                    checked={journalType === "service"}
                                    onChange={(e) => setJournalType(e.target.value)}
                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-gray-700">Service</span>
                            </label>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 w-full">
                        <button
                            onClick={handleCreateJournal}
                            disabled={isLoading}
                            className="w-1/2 sm:w-auto flex items-center gap-2 justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-4 focus:ring-green-300 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <StepCodeTag code={JOURNAL_STEP_CODES.createJournal} />
                            <FiSave className="h-5 w-5" />
                            <span className="font-semibold">{isLoading ? "Creating..." : "Create Journal"}</span>
                        </button>
                        <button
                            onClick={() => {
                                setShowForm(false);
                                setSelectedInstallers([]);
                                setInstallationDate("");
                                setInstallationTime("");
                            }}
                            disabled={isLoading}
                            className="w-1/2 sm:w-auto flex items-center justify-center px-6 py-3 bg-gray-200 text-gray-700 rounded-xl shadow hover:shadow-lg hover:bg-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-300 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="font-semibold">Cancel</span>
                        </button>
                    </div>
                </div>
            )}
            
            {journals.length > 0 ? (
                <div className="w-full overflow-x-auto">
                    <div className="mb-4 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Journal List
                        </span>
                    </div>
                    <table className="w-full min-w-max border-collapse">
                        <thead>
                            <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '30%' }}>
                                    <div className="flex items-center">
                                        <FiClock className="mr-2" />
                                        Installation Date & Time
                                    </div>
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '15%' }}>
                                    Type
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '40%' }}>
                                    <div className="flex items-center">
                                        <FiUsers className="mr-2" />
                                        Installers
                                    </div>
                                </th>
                                {canEditJournal && (
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ width: '15%' }}>
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {journals.map((journal, index) => (
                                <tr 
                                    key={journal._id || index} 
                                    className="hover:bg-gray-50 transition-colors duration-150"
                                >
                                    <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap border-r border-gray-200">
                                        {journal.createdAt ? (
                                            <div>
                                                <div className="font-medium">
                                                    {moment(journal.createdAt).format("DD/MM/YYYY")}
                                                </div>
                                                <div className="text-gray-500 text-xs">
                                                    {moment(journal.createdAt).format("HH:mm:ss")}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">N/A</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 text-sm border-r border-gray-200">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                                            journal.type === "service" 
                                                ? "bg-orange-100 text-orange-800" 
                                                : "bg-green-100 text-green-800"
                                        }`}>
                                            {journal.type || "installation"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 border-r border-gray-200">
                                        <div className="flex flex-wrap gap-2">
                                            {journal.installers && journal.installers.length > 0 ? (
                                                journal.installers.map((installer, idx) => (
                                                    <span
                                                        key={installer._id || idx}
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                                    >
                                                        {installer.name || "Unknown"}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-sm text-gray-500">No installers</span>
                                            )}
                                        </div>
                                    </td>
                                    {canEditJournal && (
                                        <td className="px-3 py-3 text-center">
                                            <div className="flex gap-2 justify-center">
                                                <button
                                                    onClick={() => handleEditJournal(journal)}
                                                    className="inline-flex items-center gap-1 p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                                                    title="Edit Journal"
                                                >
                                                    <FiEdit2 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteJournal(journal._id)}
                                                    className="inline-flex items-center gap-1 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                                    title="Delete Journal"
                                                >
                                                    <FiTrash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                        <FiUsers className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Journals Found</h3>
                    <p className="text-gray-500">
                        No installation journals have been created for this order yet.
                    </p>
                </div>
            )}

            {/* Edit Modal */}
            {editModalOpen && selectedJournal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                                Edit Journal
                            </h2>
                            <button
                                onClick={() => setEditModalOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FiX className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Order Info (Read-only) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Order
                                </label>
                                <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 font-medium">
                                    {orderData?.invoiceId || "N/A"} - {orderData?.coNumber || "N/A"}
                                </div>
                            </div>

                            {/* Installers Selection */}
                            <div>
                                <PosFieldLabel code={JOURNAL_STEP_CODES.selectInstallers} className="block text-sm font-semibold text-gray-700 mb-2">
                                    <FiUsers className="inline mr-2" />
                                    Installers *
                                </PosFieldLabel>
                                <Select
                                    isMulti
                                    options={installersOptions}
                                    value={installersOptions.filter((option) => 
                                        editFormData.installers.includes(option.value)
                                    )}
                                    onChange={(selected) => {
                                        setEditFormData({
                                            ...editFormData,
                                            installers: selected ? selected.map(s => s.value) : [],
                                        });
                                    }}
                                    placeholder="Select installers..."
                                    {...commonSelectProps}
                                />
                            </div>

                            {/* Created Date & Time */}
                            <div>
                                <PosFieldLabel code={JOURNAL_STEP_CODES.installationDateTime} className="block text-sm font-semibold text-gray-700 mb-2">
                                    <FiClock className="inline mr-2" />
                                    Created Date & Time
                                </PosFieldLabel>
                                <input
                                    type="datetime-local"
                                    value={editFormData.createdAt}
                                    onChange={(e) => 
                                        setEditFormData({
                                            ...editFormData,
                                            createdAt: e.target.value,
                                        })
                                    }
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>

                            {/* Type Selection */}
                            <div>
                                <PosFieldLabel code={JOURNAL_STEP_CODES.journalType} className="block text-sm font-semibold text-gray-700 mb-2">
                                    Type *
                                </PosFieldLabel>
                                <div className="flex gap-4">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="editJournalType"
                                            value="installation"
                                            checked={editFormData.type === "installation"}
                                            onChange={(e) => setEditFormData({
                                                ...editFormData,
                                                type: e.target.value,
                                            })}
                                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-gray-700">Installation</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="editJournalType"
                                            value="service"
                                            checked={editFormData.type === "service"}
                                            onChange={(e) => setEditFormData({
                                                ...editFormData,
                                                type: e.target.value,
                                            })}
                                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-gray-700">Service</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex gap-3 justify-end p-6 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setEditModalOpen(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateJournal}
                                className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg hover:shadow-xl"
                            >
                                Update Journal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JournalTab;

